import { getSolarRecommand } from '@/service/solar-calculator';
import { ItfEcoCartInventoryPolicy } from 'eco-design/elements/eco-cart/type.d';

export interface ProductAPIType {
    productId: string;
    variantId: string;
    img: string;
    variantImg: string;
    productTitle: string;
    skuTitle: string;
    sku: string;
    handle: string;
    price: number;
    compareAtPrice: number;
    inventoryQuantity: number;
    inventoryPolicy: ItfEcoCartInventoryPolicy;
    tag: string;
}

export interface ProductItemType extends ProductAPIType {
    link_more: string;
    link_buy: string;
    quantity: number;
    id: string;
    power: string;
    descArr: string[];
    icons: string[];
    storage: number;
    children: SolarItemType[];
    childrenIds: string[];
}

export interface SolarItemType extends ProductItemType {
    id: string;
    energy: number;
    quantity: number;
    recommand: number[];
    isActive: boolean;
    parentVariantId: string;
    storage: number;
}

export interface DataLocalType {
    solar_panels: SolarItemType[];
    products: ProductItemType[];
}

export interface RecommandType {
    [key: string]: {
        [key: string]: number[];
    };
}

// 太阳能板 Solar Panel + DELTA系列
const SP_110W: SolarItemType = {
    ...({} as SolarItemType),
    energy: 110,
    id: '110w',
};

const SP_160W: SolarItemType = {
    ...({} as SolarItemType),
    energy: 160,
    id: '160w',
};

const SP_160W_BIFACIAL: SolarItemType = {
    ...({} as SolarItemType),
    energy: 160,
    id: '160w_bifacial',
};

const SP_220W: SolarItemType = {
    ...({} as SolarItemType),
    energy: 220,
    id: '220w',
};

const SP_220W_BIFACIAL: SolarItemType = {
    ...({} as SolarItemType),
    energy: 220,
    id: '220w_bifacial',
};

const SP_400W: SolarItemType = {
    ...({} as SolarItemType),
    energy: 400,
    id: '400w',
};

const SP_400W_RIGID: SolarItemType = {
    ...({} as SolarItemType),
    energy: 400,
    id: '400w_rigid',
};

const SP_D3P: SolarItemType = {
    ...({} as SolarItemType),
    id: 'd3p',
    storage: 1.02,
};

const SP_D2: SolarItemType = {
    ...({} as SolarItemType),
    id: 'd2',
    storage: 1.04,
};

const SP_D2M: SolarItemType = {
    ...({} as SolarItemType),
    id: 'd2m',
    storage: 2.05,
};

const SP_D3MP: SolarItemType = {
    ...({} as SolarItemType),
    id: 'delta3maxplus',
    storage: 2.05,
};
const SP_D3UP: SolarItemType = {
    ...({} as SolarItemType),
    id: 'delta3ultraplus',
    storage: 3.07,
};

// 产品数据
const DELTA_3_1500: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta31500',
    storage: 1.5,
};

const DELTA_3_PLUS: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta3Plus',
    storage: 1.02,
};

const DELTA_3_MAX: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta3max',
    storage: 2.048,
};
const DELTA_3_MAX_PLUS: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta3maxplus',
    storage: 2.048,
};

const DELTA_3_CLASSIC: ProductItemType = {
    ...({} as ProductItemType),
    id: 'dp3classic',
    storage: 1.024,
};
const DELTA_3_ULTRA_PLUS: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta3ultraplus',
    storage: 3.072,
};

const DELTA_PRO_3: ProductItemType = {
    ...({} as ProductItemType),
    id: 'deltaPro3',
    storage: 4,
};

const DELTA_2: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta2',
    storage: 1.02,
};

const DELTA_2_960: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta2960',
    storage: 0.96,
};

const DELTA_2_MAX: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta2Max',
    storage: 2.05,
};

const DELTA_2_MAX_1900: ProductItemType = {
    ...({} as ProductItemType),
    id: 'delta2Max1900',
    storage: 1.9,
};

const DELTA_PRO: ProductItemType = {
    ...({} as ProductItemType),
    id: 'deltaPro',
    storage: 3.6,
};

const AC_800: ProductItemType = {
    ...({} as ProductItemType),
    id: 'ac800',
};

const GLACIER_35L_EB: ProductItemType = {
    ...({} as ProductItemType),
    id: 'glacier35lEB',
    storage: 0,
};

const GLACIER_45L_EB: ProductItemType = {
    ...({} as ProductItemType),
    id: 'glacier45lEB',
    storage: 0,
};

const GLACIER_55L_EB: ProductItemType = {
    ...({} as ProductItemType),
    id: 'glacier55lEB',
    storage: 0,
};

// 推荐搭配数量
export const RecommandMap: RecommandType = {
    delta3Plus: {
        '110w': [1, 2, 3, 4, 5, 6, 7, 8, 9],
        '160w': [1, 2, 3, 4, 5, 6],
        '220w': [1, 2, 3, 4],
        '400w': [1, 2],
    },
    delta31500: {
        '220w': [1, 2],
        '400w': [1],
    },
    delta2: {
        '110w': [1, 2, 4],
        '160w': [1, 2],
        '160w_bifacial': [1, 2],
        '220w': [1, 2],
        '220w_bifacial': [1, 2],
    },
    delta2960: {
        '110w': [1, 2, 4],
        '160w': [1, 2],
        '160w_bifacial': [1, 2],
        '220w': [1, 2],
        '220w_bifacial': [1, 2],
    },
    delta2Max: {
        '110w': [2, 3, 4],
        '160w': [1, 2, 3, 4],
        '220w': [1, 2, 3, 4],
        '220w_bifacial': [1, 2, 3, 4],
        '400w': [1, 2],
        '400w_rigid': [1, 2],
    },
    delta2Max1900: {
        '220w': [1, 2],
        '400w': [1, 2],
    },
    deltaPro3: {
        '400w': [1, 2, 3],
    },
    deltaPro: {
        '400w': [1, 2, 3],
    },
    glacier35lEB: {
        '110w': [1],
    },
    glacier45lEB: {
        '110w': [1],
    },
    glacier55lEB: {
        '110w': [1],
    },
    delta3max: {
        '220w': [1, 2],
        '400w': [1],
    },
    delta3maxplus: {
        '220w': [1, 2],
        '400w': [1],
    },
    delta3maxplus: {
        '220w': [1, 2, 3, 4],
        '400w': [1, 2],
    },
    dp3classic: {
        '110w': [1, 2, 3, 4],
        '220w': [1, 2],
    },
    delta3ultraplus: {
        '400w': [1, 2],
        '220w': [1, 2],
    },
};

// 不同国家数据
// 北美
const dataUS: DataLocalType = {
    solar_panels: [
        {
            ...SP_110W,
            productId: '4318647222345',
            variantId: '39998109941833',
        },
        {
            ...SP_160W,
            productId: '4605523198025',
            variantId: '40573832429641',
        },
        {
            ...SP_220W,
            productId: '7003545108553',
            variantId: '41670394576969',
        },
        {
            ...SP_D3P,
            productId: '7289136054345',
            variantId: '41826183249993',
        },
        {
            ...SP_D2,
            productId: '7323318190153',
            variantId: '41719074324553',
        },
        {
            ...SP_D2M,
            productId: '7049853632585',
            variantId: '41064248934473',
        },
        {
            ...SP_400W,
            productId: '6618094960713',
            variantId: '39998021697609',
        },
        {
            ...SP_D3MP,
            productId: '15386147061833',
            variantId: '54718699962441',
        },
        {
            ...SP_D3UP,
            productId: '15386143096905',
            variantId: '54718705008713',
        },
    ],
    products: [
        {
            ...DELTA_3_MAX,
            productId: '15386147061833',
            variantId: '54692162764873',
            link_more: 'https://us.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=54692162764873',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
            tag: 'NEW',
        },
        {
            ...DELTA_3_MAX_PLUS,
            productId: '15386147061833',
            variantId: '54718699962441',
            link_more: 'https://us.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=54718699962441',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
            tag: 'NEW',
        },
        {
            ...DELTA_3_CLASSIC,
            productId: '15386067730505',
            variantId: '54691871227977',
            link_more: 'https://us.ecoflow.com/products/delta-3-classic-portable-power-station?variant=54691871227977',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['110w', '220w'],
            tag: 'NEW',
        },

        {
            ...DELTA_3_PLUS,
            productId: '7289136054345',
            variantId: '41826182496329',
            link_more: 'https://us.ecoflow.com/products/delta-3-plus-portable-power-station?variant=41826182496329',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Projector', 'MiniFridge', 'CoffeeMaker', 'ElectricGrill'],
            childrenIds: ['110w', '160w', '220w'],
        },
        {
            ...DELTA_3_ULTRA_PLUS,
            productId: '15386143096905',
            variantId: '54718705008713',
            link_more: 'https://us.ecoflow.com/products/delta-3-ultra-series-portable-power-station?variant=54718705008713',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
            tag: 'NEW',
        },
        // {
        //     ...DELTA_2,
        //     productId: '7323318190153',
        //     variantId: '41719074324553',
        //     link_more: 'https://us.ecoflow.com/products/delta-2-portable-power-station?variant=41719074324553',
        //     descArr: ['', '', ''],
        //     icons: ['Light', 'Phone', 'Projector', 'MiniFridge', 'CoffeeMaker', 'ElectricGrill'],
        //     childrenIds: ['110w', '160w', '220w'],
        // },
        // {
        //     ...DELTA_2_MAX,
        //     productId: '6905537888329',
        //     variantId: '40538145095753',
        //     link_more: 'https://us.ecoflow.com/products/delta-2-max-portable-power-station?variant=40538145095753',
        //     descArr: ['', '', ''],
        //     icons: ['Light', 'Phone', 'Projector', 'MiniFridge', 'CoffeeMaker', 'ElectricGrill'],
        //     childrenIds: ['110w', '160w', '220w'],
        // },
        {
            ...AC_800,
            productId: '7049853632585',
            variantId: '41064248836169',
            link_more: 'https://us.ecoflow.com/products/800w-alternator-charger?variant=41064248836169',
            descArr: ['', '', ''],
            childrenIds: ['delta3maxplus', 'delta3ultraplus', 'd3p'],
        },
    ],
};

const dataCA: DataLocalType = {
    solar_panels: [
        {
            ...SP_110W,
            productId: '5511642644634',
            variantId: '43725961199770',
        },
        {
            ...SP_160W,
            productId: '6609119936666',
            variantId: '43762372739226',
        },
        {
            ...SP_220W,
            productId: '7296662896794',
            variantId: '43874721824922',
        },
        {
            ...SP_D3P,
            productId: '8331157766298',
            variantId: '51645141025139',
        },
        {
            ...SP_D2,
            productId: '8132015980698',
            variantId: '44676925391002',
        },
        {
            ...SP_D2M,
            productId: '8132015980698',
            variantId: '44676925456538',
        },
        {
            ...SP_400W,
            productId: '7287920001178',
            variantId: '42092770263194',
        },
        {
            ...SP_D3MP,
            productId: '15386147061833',
            variantId: '54718699962441',
        },
        {
            ...SP_D3UP,
            productId: '15386143096905',
            variantId: '54718705008713',
        },
    ],
    products: [
        {
            ...DELTA_3_MAX,
            productId: '15164532654451',
            variantId: '62060638372211',
            link_more: 'https://ca.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=62060638372211',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
            tag: 'NEW',
        },
        {
            ...DELTA_3_MAX_PLUS,
            productId: '15164532654451',
            variantId: '62106661618035',
            link_more: 'https://ca.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=62106661618035',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
            tag: 'NEW',
        },
        {
            ...DELTA_3_CLASSIC,
            productId: '15164532916595',
            variantId: '62060639584627',
            link_more: 'https://ca.ecoflow.com/products/delta-3-classic-portable-power-station?variant=62060639584627',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['110w', '220w'],
            tag: 'NEW',
        },
        {
            ...DELTA_3_PLUS,
            productId: '8331157766298',
            variantId: '51645140926835',
            link_more: 'https://ca.ecoflow.com/products/delta-3-plus-portable-power-station?variant=51645140926835',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Projector', 'MiniFridge', 'CoffeeMaker', 'ElectricGrill'],
            childrenIds: ['110w', '160w', '220w'],
        },
        {
            ...DELTA_3_ULTRA_PLUS,
            productId: '15164531016051',
            variantId: '62106740556147',
            link_more: 'https://ca.ecoflow.com/products/delta-3-ultra-series-portable-power-station?variant=62106740556147',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
            tag: 'NEW',
        },
        // {
        //     ...DELTA_2,
        //     productId: '7440912384154',
        //     variantId: '43725896056986',
        //     link_more: 'https://ca.ecoflow.com/products/delta-2-portable-power-station?variant=43725896056986',
        //     descArr: ['', '', ''],
        //     icons: ['Light', 'Phone', 'Projector', 'MiniFridge', 'CoffeeMaker', 'ElectricGrill'],
        //     childrenIds: ['110w', '160w', '220w'],
        // },
        // {
        //     ...DELTA_2_MAX,
        //     productId: '7825964400794',
        //     variantId: '43592212775066',
        //     link_more: 'https://ca.ecoflow.com/products/delta-2-max-portable-power-station?variant=43592212775066',
        //     descArr: ['', '', ''],
        //     icons: ['Light', 'Phone', 'Projector', 'MiniFridge', 'CoffeeMaker', 'ElectricGrill'],
        //     childrenIds: ['110w', '160w', '220w'],
        // },
        {
            ...AC_800,
            productId: '8132015980698',
            variantId: '44676925358234',
            link_more: 'https://ca.ecoflow.com/products/800w-alternator-charger?variant=44676925358234',
            descArr: ['', '', ''],
            childrenIds: ['delta3maxplus', 'delta3ultraplus', 'd3p'],
        },
    ],
};

// 欧洲
const dataEU: DataLocalType = {
    solar_panels: [
        {
            ...SP_160W,
            productId: '10106925056343',
            variantId: '50969351487831',
        },
        {
            ...SP_220W,
            productId: '10107077493079',
            variantId: '52180835959127',
        },
        {
            ...SP_400W,
            productId: '10265047433559',
            variantId: '51634686198103',
        },
    ],
    products: [
        {
            ...DELTA_3_MAX_PLUS,
            productId: '10301812244823',
            variantId: '51787034558807',
            link_more: 'https://eu.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=51787034558807',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_3_PLUS,
            productId: '9744704930135',
            variantId: '49573797593431',
            link_more: 'https://eu.ecoflow.com/products/delta-3-series-portable-power-station?variant=49573797593431',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
        {
            ...DELTA_PRO_3,
            productId: '9488706797911',
            variantId: '48911033270615',
            link_more: 'https://eu.ecoflow.com/products/delta-pro-3-portable-power-station?variant=48911033270615',
            descArr: ['', '', ''],
            icons: ['Light', 'CarFridge', 'Phone', 'ElectricBlanket', 'ElectricGrill', 'AirConditioner'],
            childrenIds: ['400w'],
        },
        {
            ...DELTA_2_MAX,
            productId: '8414996627799',
            variantId: '46790633881943',
            link_more: 'https://eu.ecoflow.com/products/delta-2-max-portable-power-station?variant=46790633881943',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['160w', '220w', '400w'],
        },
        {
            ...DELTA_2,
            productId: '7403706253476',
            variantId: '42596003512484',
            link_more: 'https://eu.ecoflow.com/products/delta-2-portable-power-station?variant=42596003512484',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
    ],
};

const dataUK: DataLocalType = {
    solar_panels: [
        {
            ...SP_160W,
            productId: '9947421114707',
            variantId: '51130710917459',
        },
        {
            ...SP_220W,
            productId: '9966309441875',
            variantId: '51216552886611',
        },
        {
            ...SP_400W,
            productId: '10123990729043',
            variantId: '51828861469011',
        },
    ],
    products: [
        {
            ...DELTA_3_MAX_PLUS,
            productId: '10279052902739',
            variantId: '52472460640595',
            link_more: 'https://uk.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=52472460640595',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_3_PLUS,
            productId: '9616877420883',
            variantId: '49833568993619',
            link_more: 'https://uk.ecoflow.com/products/delta-3-series-portable-power-station?variant=49833568993619',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
        {
            ...DELTA_PRO_3,
            productId: '9430243180883',
            variantId: '49296152723795',
            link_more: 'https://uk.ecoflow.com/products/delta-pro-3-portable-power-station?variant=49296152723795',
            descArr: ['', '', ''],
            icons: ['Light', 'CarFridge', 'Phone', 'ElectricBlanket', 'ElectricGrill', 'AirConditioner'],
            childrenIds: ['400w'],
        },
        {
            ...DELTA_2_MAX,
            productId: '8416984367443',
            variantId: '46679851139411',
            link_more: 'https://uk.ecoflow.com/products/delta-2-max-portable-power-station?variant=46679851139411',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['160w', '220w', '400w'],
        },
        {
            ...DELTA_2,
            productId: '7335539867840',
            variantId: '43099247608000',
            link_more: 'https://uk.ecoflow.com/products/delta-2-portable-power-station?variant=43099247608000',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
    ],
};

const dataDE: DataLocalType = {
    solar_panels: [
        {
            ...SP_160W,
            productId: '15321906676093',
            variantId: '56293334679933',
        },
        {
            ...SP_220W,
            productId: '15321895600509',
            variantId: '56293274190205',
        },
        {
            ...SP_400W,
            productId: '15536875929981',
            variantId: '56954370294141',
        },
    ],
    products: [
        {
            ...DELTA_3_MAX_PLUS,
            productId: '15562538451325',
            variantId: '57020101296509',
            link_more: 'https://de.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=57020101296509',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_3_PLUS,
            productId: '14848608960847',
            variantId: '53707200004431',
            link_more: 'https://de.ecoflow.com/products/delta-3-series-portable-power-station?variant=53707200004431',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
        {
            ...DELTA_PRO_3,
            productId: '9480253407567',
            variantId: '49424786358607',
            link_more: 'https://de.ecoflow.com/products/delta-pro-3-portable-power-station?variant=49424786358607',
            descArr: ['', '', ''],
            icons: ['Light', 'CarFridge', 'Phone', 'ElectricBlanket', 'ElectricGrill', 'AirConditioner'],
            childrenIds: ['400w'],
        },
        {
            ...DELTA_2_MAX,
            productId: '8513034813775',
            variantId: '46739567706447',
            link_more: 'https://de.ecoflow.com/products/delta-2-max-portable-power-station?variant=46739567706447',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['160w', '220w', '400w'],
        },
        {
            ...DELTA_2,
            productId: '7742214308087',
            variantId: '43148797739255',
            link_more: 'https://de.ecoflow.com/products/delta-2-portable-power-station?variant=43148797739255',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
    ],
};

const dataFR: DataLocalType = {
    solar_panels: [
        {
            ...SP_160W,
            productId: '9966244135259',
            variantId: '50834554093915',
        },
        {
            ...SP_220W,
            productId: '9976213274971',
            variantId: '50883301867867',
        },
        {
            ...SP_400W,
            productId: '10084432576859',
            variantId: '51300297998683',
        },
    ],
    products: [
        {
            ...DELTA_3_MAX_PLUS,
            productId: '10110678139227',
            variantId: '51408465002843',
            link_more: 'https://fr.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=51408465002843',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_3_PLUS,
            productId: '9594942095707',
            variantId: '49526901866843',
            link_more: 'https://fr.ecoflow.com/products/delta-3-series-portable-power-station?variant=49526901866843',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
        {
            ...DELTA_PRO_3,
            productId: '9375271846235',
            variantId: '48799611879771',
            link_more: 'https://fr.ecoflow.com/products/delta-pro-3-portable-power-station?variant=48799611879771',
            descArr: ['', '', ''],
            icons: ['Light', 'CarFridge', 'Phone', 'ElectricBlanket', 'ElectricGrill', 'AirConditioner'],
            childrenIds: ['400w'],
        },
        {
            ...DELTA_2_MAX,
            productId: '8431233794395',
            variantId: '46793751789915',
            link_more: 'https://fr.ecoflow.com/products/delta-2-max-portable-power-station?variant=46793751789915',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['160w', '220w', '400w'],
        },
        {
            ...DELTA_2,
            productId: '7838178017506',
            variantId: '43231886704866',
            link_more: 'https://fr.ecoflow.com/products/delta-2-portable-power-station?variant=43231886704866',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
    ],
};

const dataIT: DataLocalType = {
    solar_panels: [
        {
            ...SP_160W,
            productId: '14940805202296',
            variantId: '55188402078072',
        },
        {
            ...SP_220W,
            productId: '14940806021496',
            variantId: '55188408238456',
        },
        {
            ...SP_400W,
            productId: '14963038814584',
            variantId: '55270023627128',
        },
    ],
    products: [
        {
            ...DELTA_3_MAX_PLUS,
            productId: '14969542836600',
            variantId: '55289474253176',
            link_more: 'https://it.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=55289474253176',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_3_PLUS,
            productId: '14845588341112',
            variantId: '54819109306744',
            link_more: 'https://it.ecoflow.com/products/delta-3-series-portable-power-station?variant=54819109306744',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
        {
            ...DELTA_PRO_3,
            productId: '9646835597661',
            variantId: '52783177990493',
            link_more: 'https://it.ecoflow.com/products/delta-pro-3-portable-power-station?variant=52783177990493',
            descArr: ['', '', ''],
            icons: ['Light', 'CarFridge', 'Phone', 'ElectricBlanket', 'ElectricGrill', 'AirConditioner'],
            childrenIds: ['400w'],
        },
        {
            ...DELTA_2_MAX,
            productId: '8415773360477',
            variantId: '46891073274205',
            link_more: 'https://it.ecoflow.com/products/delta-2-max-portable-power-station?variant=46891073274205',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['160w', '220w', '400w'],
        },
        {
            ...DELTA_2,
            productId: '7778985246961',
            variantId: '43166053925105',
            link_more: 'https://it.ecoflow.com/products/delta-2-portable-power-station?variant=43166053925105',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
    ],
};

const dataES: DataLocalType = {
    solar_panels: [
        {
            ...SP_160W,
            productId: '9976249975131',
            variantId: '50883397845339',
        },
        {
            ...SP_220W,
            productId: '9976244633947',
            variantId: '50883390636379',
        },
        {
            ...SP_400W,
            productId: '10084460560731',
            variantId: '51300335288667',
        },
    ],
    products: [
        {
            ...DELTA_3_MAX_PLUS,
            productId: '10110680498523',
            variantId: '51408468836699',
            link_more: 'https://es.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=51408468836699',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_3_PLUS,
            productId: '9594953924955',
            variantId: '49526969139547',
            link_more: 'https://es.ecoflow.com/products/delta-3-series-portable-power-station?variant=49526969139547',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
        {
            ...DELTA_PRO_3,
            productId: '9375312970075',
            variantId: '48799731024219',
            link_more: 'https://es.ecoflow.com/products/delta-pro-3-portable-power-station?variant=48799731024219',
            descArr: ['', '', ''],
            icons: ['Light', 'CarFridge', 'Phone', 'ElectricBlanket', 'ElectricGrill', 'AirConditioner'],
            childrenIds: ['400w'],
        },
        {
            ...DELTA_2_MAX,
            productId: '8435033899355',
            variantId: '46787970269531',
            link_more: 'https://es.ecoflow.com/products/delta-2-max-portable-power-station?variant=46787970269531',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['160w', '220w', '400w'],
        },
        {
            ...DELTA_2,
            productId: '7766079799518',
            variantId: '43293121380574',
            link_more: 'https://es.ecoflow.com/products/delta-2-portable-power-station?variant=43293121380574',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
    ],
};

const dataNL: DataLocalType = {
    solar_panels: [
        {
            ...SP_160W,
            productId: '9935714353428',
            variantId: '51427564355860',
        },
        {
            ...SP_220W,
            productId: '9935714386196',
            variantId: '51427570286868',
        },
        {
            ...SP_400W,
            productId: '9935698034964',
            variantId: '51194372948244',
        },
    ],
    products: [
        {
            ...DELTA_3_MAX_PLUS,
            productId: '10080513949972',
            variantId: '51820094816532',
            link_more: 'https://nl.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=51820094816532',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_3_PLUS,
            productId: '9935710978324',
            variantId: '51194415317268',
            link_more: 'https://nl.ecoflow.com/products/delta-3-series-portable-power-station?variant=51194415317268',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
        {
            ...DELTA_PRO_3,
            productId: '9935709536532',
            variantId: '51194412302612',
            link_more: 'https://nl.ecoflow.com/products/delta-pro-3-portable-power-station?variant=51194412302612',
            descArr: ['', '', ''],
            icons: ['Light', 'CarFridge', 'Phone', 'ElectricBlanket', 'ElectricGrill', 'AirConditioner'],
            childrenIds: ['400w'],
        },
        {
            ...DELTA_2_MAX,
            productId: '9935701901588',
            variantId: '51194384351508',
            link_more: 'https://nl.ecoflow.com/products/delta-2-max-portable-power-station?variant=51194384351508',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['160w', '220w', '400w'],
        },
        {
            ...DELTA_2,
            productId: '9935699575060',
            variantId: '51194377306388',
            link_more: 'https://nl.ecoflow.com/products/delta-2-portable-power-station?variant=51194377306388',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w'],
        },
    ],
};

// ROW
const dataAU: DataLocalType = {
    solar_panels: [
        {
            ...SP_160W,
            productId: '8228927897798',
            variantId: '44409522553030',
        },
        {
            ...SP_220W,
            productId: '8258480472262',
            variantId: '43982554431686',
        },
        {
            ...SP_400W,
            productId: '7238909657286',
            variantId: '42994548211910',
        },
    ],
    products: [
        {
            ...DELTA_2,
            productId: '7756517146822',
            variantId: '42768126181574',
            link_more: 'https://au.ecoflow.com/collections/new-product-launch/products/delta-2-portable-power-station',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Projector', 'MiniFridge', 'CoffeeMaker', 'ElectricGrill'],
            childrenIds: ['160w', '220w'],
        },
        {
            ...DELTA_2_MAX,
            productId: '7914502422726',
            variantId: '43045698371782',
            link_more: 'https://au.ecoflow.com/products/delta-2-max-portable-power-station',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Refrigerator', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_2_MAX_1900,
            productId: '8236481118406',
            variantId: '43873257357510',
            link_more: 'https://au.ecoflow.com/products/delta-2-max-1900-portable-power-station',
            descArr: ['', ''],
            icons: ['Light', 'Phone', 'Refrigerator'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_3_PLUS,
            productId: '8435739197638',
            variantId: '44378294452422',
            link_more: 'https://au.ecoflow.com/products/ecoflow-delta-3-plus-portable-power-station',
            descArr: ['', ''],
            icons: ['Light', 'Phone', 'Refrigerator', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_3_1500,
            productId: '8315583430854',
            variantId: '44211129614534',
            link_more: 'https://au.ecoflow.com/products/delta-3-1500-portable-power-station',
            descArr: ['', ''],
            icons: ['Light', 'Phone', 'Refrigerator', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['220w', '400w'],
        },
        {
            ...DELTA_PRO_3,
            productId: '8311879925958',
            variantId: '44062870503622',
            link_more: 'https://au.ecoflow.com/products/ecoflow-delta-pro-3-portable-power-station',
            descArr: ['', ''],
            icons: ['Light', 'Phone', 'Refrigerator', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['400w'],
        },
        {
            ...DELTA_PRO,
            productId: '7220911505606',
            variantId: '42736222142662',
            link_more: 'https://au.ecoflow.com/products/delta-pro-portable-power-station',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['400w'],
        },
    ],
};

const dataZA: DataLocalType = {
    solar_panels: [
        {
            ...SP_110W,
            productId: '7564101976273',
            variantId: '42489077334225',
        },
        {
            ...SP_160W,
            productId: '7027757023441',
            variantId: '41611172741329',
        },
        {
            ...SP_160W_BIFACIAL,
            productId: '7468127224017',
            variantId: '42234708820177',
        },
        {
            ...SP_220W,
            productId: '7609705595089',
            variantId: '42613373141201',
        },
        {
            ...SP_220W_BIFACIAL,
            productId: '7027754926289',
            variantId: '41630471913681',
        },
        {
            ...SP_400W,
            productId: '7027755253969',
            variantId: '41630472405201',
        },
        {
            ...SP_400W_RIGID,
            productId: '7146943840465',
            variantId: '41611173462225',
        },
    ],
    products: [
        {
            ...DELTA_2,
            productId: '7187057803473',
            variantId: '41620506575057',
            link_more: 'https://za.ecoflow.com/products/delta-2-portable-power-station?variant=41620506575057',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Projector', 'MiniFridge', 'CoffeeMaker', 'ElectricGrill'],
            childrenIds: ['110w', '160w', '160w_bifacial', '220w_bifacial'],
        },
        {
            ...DELTA_2_960,
            productId: '7510978953425',
            variantId: '42363066450129',
            link_more: 'https://za.ecoflow.com/products/ecoflow-delta-2-960-portable-power-station?variant=42363066450129',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Projector', 'MiniFridge', 'CoffeeMaker', 'ElectricGrill'],
            childrenIds: ['110w', '160w', '220w', '220w_bifacial'],
        },
        {
            ...DELTA_2_MAX,
            productId: '7352407982289',
            variantId: '41935518335185',
            link_more: 'https://za.ecoflow.com/products/delta-2-max-portable-power-station?variant=41935518335185',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'Projector', 'MiniFridge', 'CoffeeMaker', 'ElectricGrill'],
            childrenIds: ['220w', '220w_bifacial', '400w', '400w_rigid'],
        },
        {
            ...DELTA_PRO_3,
            productId: '7639611900113',
            variantId: '42744383340753',
            link_more: 'https://za.ecoflow.com/products/delta-pro-3-portable-power-station?variant=42744383340753',
            descArr: ['', '', ''],
            icons: ['Light', 'Phone', 'CarFridge', 'CoffeeMaker', 'Microwave', 'AirConditioner'],
            childrenIds: ['400w'],
        },
    ],
};

const dataKR: DataLocalType = {
    solar_panels: [
        {
            ...SP_110W,
            productId: '7429921112233',
            variantId: '44206195343529',
        },
    ],
    products: [
        {
            ...GLACIER_35L_EB,
            productId: '8586234855593',
            variantId: '45935109996713',
            link_more: 'https://kr.ecoflow.com/products/glacier-classic-portable-fridge-freezer?variant=45935109996713',
            descArr: ['', '', '', ''],
            icons: [],
            childrenIds: ['110w'],
        },
        {
            ...GLACIER_45L_EB,
            productId: '8586234855593',
            variantId: '45935110029481',
            link_more: 'https://kr.ecoflow.com/products/glacier-classic-portable-fridge-freezer?variant=45935110029481',
            descArr: ['', '', '', ''],
            icons: [],
            childrenIds: ['110w'],
        },
        {
            ...GLACIER_55L_EB,
            productId: '8586234855593',
            variantId: '45935110062249',
            link_more: 'https://kr.ecoflow.com/products/glacier-classic-portable-fridge-freezer?variant=45935110062249',
            descArr: ['', '', '', ''],
            icons: [],
            childrenIds: ['110w'],
        },
    ],
};

const dataAll: { [key: string]: DataLocalType } = {
    us: dataUS,
    ca: dataCA,
    eu: dataEU,
    uk: dataUK,
    de: dataDE,
    fr: dataFR,
    it: dataIT,
    es: dataES,
    nl: dataNL,
    au: dataAU,
    za: dataZA,
    kr: dataKR,
};

/**
 *
 * @param locale 国家locale
 */
export const getLocalData = (locale: string) => dataAll[locale] || null;

/**
 *  有效光照时常平均为每日6小时, 但是欧洲六站为5h，日本4h
 * @param power 太阳能板功率
 * @param n 太阳能板子数量
 * @returns 生成的能量
 */
export const calcEnergyGeneration = (power: number, n: number, locale: string) =>
    Math.round(((power * 100) / 1000) * (['eu', 'uk', 'de', 'fr', 'it', , 'es', 'nl'].includes(locale) ? 5 : ['jp'].includes(locale) ? 4 : 6) * n) / 100;

// API接口
export const getAsynItem = async (item: SolarItemType | ProductItemType, locale: string): Promise<SolarItemType | ProductItemType> => {
    const { productId, variantId } = item;
    if (!productId || !variantId) return item;
    return await new Promise(async (resolve, reject) => {
        try {
            const res = await getSolarRecommand({ productId, variantId }, locale);
            if (res?.code === '0') {
                if (Object.keys(res?.data)?.length === 0) {
                    res.data = {
                        inventoryPolicy: 'deny',
                        inventoryQuantity: 0,
                    };
                }
                if (res?.data?.handle) {
                    item.link_more = `https://${locale}.ecoflow.com/products/${res?.data?.handle}?variant=${variantId}`;
                }
                item.link_buy = `https://${locale}.ecoflow.com/cart/${variantId}:1?channel=buy_button`;
                return resolve({ ...item, ...res?.data });
            } else {
                console.error(res?.message);
                return reject({ ...item });
            }
        } catch (e) {
            console.error(e);
            return reject({ ...item });
        }
    });
};

export const BungleImgMap: {
    [key: string]: {
        [key: string]: {
            [key: string]: string;
        };
    };
} = {
    delta3Plus: {
        '110w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768208086/d3p_110w.png',
        },
        '160w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768208280/d3p_160w.png',
        },
        '220w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768208464/d3p_220w.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384240/d3p_220w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758597337018/d3p_220w_eu.png',
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617252379/d3p_220w_au.png',
        },
        '400w': {
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617252429/d3p_400w_au.png',
        },
    },
    delta31500: {
        '220w': {
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617295507/d3_1500_220w_au.png',
        },
        '400w': {
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617295913/d3_1500_400w_au.png',
        },
    },
    delta2: {
        '110w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768206918/d2_110w.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613410139/d2_110w_za.png',
        },
        '160w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768207067/d2_160w.png',
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617079409/d2_160w_au.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613515111/d2_160w_za.png',
        },
        '160w_bifacial': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613514373/d2_160w_bifacial_za.png',
        },
        '220w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768207250/d2_220w.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758597337013/d2_220w_eu.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758597337013/d2_220w_eu.png', // 未提供英规，用欧规
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617079453/d2_220w_au.png',
        },
        '220w_bifacial': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613515349/d2_220w_bifacial_za.png',
        },
    },
    delta2960: {
        '110w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613411595/d2_960_110w_za.png',
        },
        '160w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613416420/d2_960_160w_za.png',
        },
        '220w': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613627585/d2_960_220w_za.png',
        },
        '220w_bifacial': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613411379/d2_960_220w_bifacial_za.png',
        },
    },
    delta2Max: {
        '110w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768207431/d2m_110w*2.png',
        },
        '160w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768207648/d2m_160w.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758597337018/d2m_160w_eu.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758597337018/d2m_160w_eu.png', // 未提供英规，用欧规
        },
        '220w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1755768207839/d2x_220w.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758598200405/d2m_220w_eu.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758598200405/d2m_220w_eu.png', // 未提供英规，用欧规
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617157014/d2m_220w_au.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613735289/d2m_220w_za.png',
        },
        '220w_bifacial': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613417200/d2m_220w_bifacial_za.png',
        },
        '400w': {
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758195143503/d2m_400w_eu.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758195143503/d2m_400w_eu.png',
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617157054/d2m_400w_au.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613411138/d2m_400w_za.png',
        },
        '400w_rigid': {
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613416639/d2m_400w_rigid_za.png',
        },
    },
    delta2Max1900: {
        '220w': {
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617200701/d2m_1900_220w_au.png',
        },
        '400w': {
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617200854/d2m_1900_400w_au.png',
        },
    },
    deltaPro3: {
        '400w': {
            uk: 'https://websiteoss.ecoflow.com/upload/image/1758596384243/dp3_400w_uk.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1758597337138/dp3_400w_eu.png',
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617362858/dp3_400w_au.png',
            za: 'https://websiteoss.ecoflow.com/upload/image/1758613410717/dp3_400w_za.png',
        },
    },
    deltaPro: {
        '400w': {
            au: 'https://websiteoss.ecoflow.com/upload/image/1758617362311/dp_400w_au.png',
        },
    },
    ac800: {
        d3p: {
            en: 'https://websiteoss.ecoflow.com/upload/image/1756353217796/d3p_ac800.png',
        },
        d2: {
            en: 'https://websiteoss.ecoflow.com/upload/image/1756353217485/d2_ac800.png',
        },
        d2m: {
            en: 'https://websiteoss.ecoflow.com/upload/image/1756353217611/d2m_ac800.png',
        },
        delta3maxplus: {
            en: 'https://websiteoss.ecoflow.com/upload/image/1758947110154/d3mp_800.png',
        },
        delta3ultraplus: {
            en: 'https://websiteoss.ecoflow.com/upload/image/1758947110323/d3up_800.png',
        },
    },
    glacier35lEB: {
        '110w': {
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599362919/glacier_35L_110w_kr.png',
        },
    },
    glacier45lEB: {
        '110w': {
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599363107/glacier_45L_110w_kr.png',
        },
    },
    glacier55lEB: {
        '110w': {
            kr: 'https://websiteoss.ecoflow.com/upload/image/1758599363106/glacier_55L_110w_kr.png',
        },
    },
    delta3max: {
        '220w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1758944697651/d3m_220w.png',
        },
        '400w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1758944697820/d3m_400w.png',
        },
    },
    delta3maxplus: {
        '220w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1758944790603/d3mp_220w.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1765965934697/D3MP%2B1_220W%20SP_%E6%AC%A7%E8%A7%84.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1765965851635/D3MP%2B1_220W%20SP_%E8%8B%B1%E8%A7%84.png',
        },
        '400w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1758944790785/d3mp_400w.png',
            eu: 'https://websiteoss.ecoflow.com/upload/image/1765965934660/D3MP%2B1_400W%20SP_%E6%AC%A7%E8%A7%84.png',
            uk: 'https://websiteoss.ecoflow.com/upload/image/1765965851813/D3MP%2B1_400W%20SP_%E8%8B%B1%E8%A7%84.png',
        },
    },
    delta3ultraplus: {
        '220w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1758944854482/d3up_220w.png',
        },
        '400w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1758944854659/d3up_400w.png',
        },
    },
    dp3classic: {
        '110w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1760150064178/d3c_110w.png',
        },
        '220w': {
            en: 'https://websiteoss.ecoflow.com/upload/image/1758943947070/d3c_220w.png',
        },
    },
};

export const formatNumber = (num: number, locale: string) => {
    if (typeof num !== 'number' || !num) return num;
    // 千分位分隔符为英文句号的站点
    const THOUSANDS_SEPARATOR_PERIOD_SITES: string[] = ['eu', 'it', 'es', 'nl'];
    // 千分位分隔符为空格的站点
    const THOUSANDS_SEPARATOR_SPACE_SITES: string[] = ['de', 'fr'];
    // 小数点分割符为逗号的站点
    const DECIMAL_SEPARATOR_COMMA_SITES: string[] = ['de', 'fr', 'it', 'es', 'nl'];
    const _getThousandsSeparator = (locale: string) => {
        if (THOUSANDS_SEPARATOR_PERIOD_SITES.includes(locale)) {
            return '.';
        }
        if (THOUSANDS_SEPARATOR_SPACE_SITES.includes(locale)) {
            return '\u202F';
        }
        return ',';
    };

    const _getDecimalSeparator = (locale: string) => {
        if (DECIMAL_SEPARATOR_COMMA_SITES.includes(locale)) {
            return ',';
        }
        return '.';
    };

    let strValue: string = num.toString();
    const thousandsSeparator = _getThousandsSeparator(locale);
    const decimalSeparator = _getDecimalSeparator(locale);

    if (DECIMAL_SEPARATOR_COMMA_SITES.includes(locale)) {
        strValue = strValue.replace('.', decimalSeparator);
    }
    const reg = /(\d)(?=(\d{3})+$)/g;
    strValue = strValue.replace(reg, `$1${thousandsSeparator}`);
    return strValue;
};
